/**
 * 글(블로그) 계측 — 이벤트 이름과 속성의 정본.
 * @description 글은 `slug` 로 식별한다. 주소가 곧 이름이라 대시보드에서 바로 읽힌다. 같은 글의
 *   언어별 성적은 `language`(실제로 나간 언어)로 가른다.
 *   `Blog Read Completed` 는 본문 끝까지 스크롤했을 때 한 번 — 조회 대비 완독률이 글의 성적이다.
 */
import type { BlogDetail, BlogItem } from "./dtos";
import { trackEvent } from "@/shared/lib/analytics/amplitude";
import type { ShareOutcome } from "@/shared/lib/share";

function blogProps(post: BlogItem): Record<string, unknown> {
  return {
    slug: post.slug,
    title: post.title,
    itemType: post.itemType,
    category: post.category?.slug,
    author: post.author.name,
    language: post.language ?? null,
  };
}

export function trackBlogViewed(
  post: BlogDetail,
  readingMinutes: number,
): void {
  trackEvent("Blog Viewed", { ...blogProps(post), readingMinutes });
}

export function trackBlogReadCompleted(post: BlogDetail): void {
  trackEvent("Blog Read Completed", blogProps(post));
}

export function trackBlogShared(post: BlogDetail, outcome: ShareOutcome): void {
  trackEvent("Blog Shared", {
    ...blogProps(post),
    placement: "header",
    outcome,
  });
}

export function trackBlogAppOpened(post: BlogDetail): void {
  trackEvent("Blog App Opened", {
    ...blogProps(post),
    placement: "footer-cta",
  });
}

export function trackBlogRelatedClicked(
  from: BlogDetail,
  to: BlogItem,
  index: number,
): void {
  trackEvent("Blog Related Clicked", {
    ...blogProps(from),
    toSlug: to.slug,
    toTitle: to.title,
    index,
  });
}

/** 헤더의 언어 전환 링크를 눌렀다 — [post] 는 지금 보던 판, [to] 는 옮겨 갈 언어 */
export function trackBlogLanguageSwitched(post: BlogDetail, to: string): void {
  trackEvent("Blog Language Switched", {
    ...blogProps(post),
    placement: "header",
    to,
  });
}
